import { Link } from "react-router-dom";

export type PaymentRow = {
  id: string;
  client_name: string;
  payment_date: string;
  method: string;
  amount: number;
  status: string;
  contract_number: string | null;
};

interface Props {
  payments: PaymentRow[];
}

const STATUS_STYLES: Record<string, { color: string; bg: string; label: string }> = {
  confirmed:  { color: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200",  label: "Confirmed" },
  paid:       { color: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200",  label: "Paid" },
  completed:  { color: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200",  label: "Completed" },
  pending:    { color: "text-amber-700",   bg: "bg-amber-50 border border-amber-200",     label: "Pending" },
  failed:     { color: "text-red-700",     bg: "bg-red-50 border border-red-200",         label: "Failed" },
  refunded:   { color: "text-violet-700",  bg: "bg-violet-50 border border-violet-200",   label: "Refunded" },
  void:       { color: "text-slate-500",   bg: "bg-slate-50 border border-slate-200",     label: "Void" },
  cancelled:  { color: "text-slate-500",   bg: "bg-slate-50 border border-slate-200",     label: "Cancelled" },
};

const METHOD_ICONS: Record<string, string> = {
  cash:     "ri-money-dollar-circle-line",
  transfer: "ri-bank-line",
  card:     "ri-bank-card-line",
  zelle:    "ri-exchange-dollar-line",
  venmo:    "ri-smartphone-line",
  check:    "ri-file-list-3-line",
  paypal:   "ri-paypal-line",
  other:    "ri-more-2-line",
};

function formatDate(dateStr: string): string {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr + (dateStr.includes("T") ? "" : "T00:00:00")).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function DashboardPayments({ payments }: Props) {
  const CONFIRMED = ["confirmed", "paid", "completed"];
  const pendingCount   = payments.filter((p) => p.status === "pending").length;
  const totalConfirmed = payments
    .filter((p) => CONFIRMED.includes(p.status))
    .reduce((s, p) => s + Number(p.amount), 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <span className="w-6 h-6 flex items-center justify-center rounded-lg bg-emerald-50">
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xs"></i>
          </span>
          <h3 className="text-[#0f172a] font-semibold text-sm">Recent Payments</h3>
          {pendingCount > 0 && (
            <span className="bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {pendingCount} pending
            </span>
          )}
        </div>
        <Link
          to="/admin/payments"
          className="text-[#C8A96E] hover:text-[#9a7840] text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          View all →
        </Link>
      </div>

      {/* Summary strip */}
      {payments.length > 0 && totalConfirmed > 0 && (
        <div className="px-5 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
          <i className="ri-check-double-line text-emerald-500 text-xs"></i>
          <span className="text-emerald-700 text-xs font-semibold">${totalConfirmed.toLocaleString()}</span>
          <span className="text-slate-400 text-xs">confirmed in this view</span>
        </div>
      )}

      {/* List */}
      <div className="flex-1 divide-y divide-slate-50">
        {payments.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10">
            <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50">
              <i className="ri-money-dollar-circle-line text-slate-300 text-lg"></i>
            </span>
            <p className="text-slate-400 text-sm">No payments yet</p>
          </div>
        ) : (
          payments.map((pay) => {
            const cfg        = STATUS_STYLES[pay.status] ?? STATUS_STYLES.pending;
            const methodIcon = METHOD_ICONS[pay.method?.toLowerCase()] ?? METHOD_ICONS.other;
            const isVoided   = pay.status === "void" || pay.status === "cancelled";

            return (
              <div
                key={pay.id}
                className={`px-5 py-3.5 flex items-center gap-3 hover:bg-slate-50/80 transition-colors ${isVoided ? "opacity-50" : ""}`}
              >
                {/* Method icon */}
                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                  <i className={`${methodIcon} text-[#C8A96E] text-sm`}></i>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-[#0f172a] text-sm font-medium truncate">{pay.client_name}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-slate-400 text-xs capitalize">{pay.method}</span>
                    {pay.contract_number && (
                      <span className="text-slate-300 text-xs">· {pay.contract_number}</span>
                    )}
                    <span className="text-slate-300 text-xs">· {formatDate(pay.payment_date)}</span>
                  </div>
                </div>

                {/* Amount + status */}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`font-bold text-sm ${isVoided ? "line-through text-slate-400" : "text-[#0f172a]"}`}>
                    ${Number(pay.amount).toLocaleString()}
                  </span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                    {cfg.label}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {payments.length > 0 && (
        <div className="px-5 py-3 border-t border-slate-100 shrink-0">
          <Link
            to="/admin/payments"
            className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-xs font-medium text-slate-500 hover:text-[#0f172a] hover:bg-slate-50 border border-slate-200 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line"></i>
            View all payments
          </Link>
        </div>
      )}
    </div>
  );
}